import React, { useState } from 'react';
import { Edit2, Trash2, Clock, Copy, Check, Pause, Calendar, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { JiraBadge, useProjectJiraUrl } from '../JiraBadge';
import { RichTextContent } from '../ui/RichTextEditor';
import {
  Task,
  TaskPriorityLabels,
  TaskStatusLabels,
  TaskPriorityColors,
  TaskStatusColors
} from '../../types/task';
import { formatTaskDate } from '../../utils/dateUtils';

interface TaskCardProps {
  task: Task;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
  onStatusChange: (taskId: string, newStatus: Task['status']) => void;
  onDuplicate?: (task: Task) => void;
  showSelection?: boolean;
  isSelected?: boolean;
  onSelect?: (taskId: string) => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onDelete,
  onStatusChange,
  onDuplicate,
  showSelection = false,
  isSelected = false,
  onSelect
}) => {
  const [showActions, setShowActions] = useState(false);
  const jiraUrl = useProjectJiraUrl(task.projectId);

  const handleStatusChange = (newStatus: Task['status']) => {
    onStatusChange(task._id, newStatus);
    setShowActions(false);
  };

  const getStatusButton = () => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (task.status === 'encerrada') {
        handleStatusChange('pendente');
      } else {
        handleStatusChange('encerrada');
      }
    };

    const baseClasses = "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 hover:scale-110";

    switch (task.status) {
      case 'encerrada':
        return (
          <button
            onClick={handleClick}
            className={`${baseClasses} bg-green-500 border-green-500`}
            title="Clique para reativar"
          >
            <Check className="w-4 h-4 text-white" />
          </button>
        );
      case 'em_progresso':
        return (
          <button
            onClick={handleClick}
            className={`${baseClasses} border-amber-500 bg-amber-500/20`}
            title="Clique para encerrar"
          >
            <Play className="w-3 h-3 text-amber-500 ml-0.5" />
          </button>
        );
      case 'pausado':
        return (
          <button
            onClick={handleClick}
            className={`${baseClasses} border-yellow-500 bg-yellow-500/20`}
            title="Clique para encerrar"
          >
            <Pause className="w-3 h-3 text-yellow-500" />
          </button>
        );
      default: // pendente
        return (
          <button
            onClick={handleClick}
            className={`${baseClasses} border-green-500 bg-transparent hover:bg-green-500/10`}
            title="Clique para encerrar"
          >
          </button>
        );
    }
  };

  const getStatusActions = () => {
    const actions = [];
    
    if (task.status !== 'pendente') {
      actions.push(
        <button
          key="pendente"
          onClick={() => handleStatusChange('pendente')}
          className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded"
        >
          Marcar como Pendente
        </button>
      );
    }
    
    if (task.status !== 'pausado') {
      actions.push(
        <button
          key="pausado"
          onClick={() => handleStatusChange('pausado')}
          className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded"
        >
          Pausar Tarefa
        </button>
      );
    }
    
    if (task.status !== 'encerrada') {
      actions.push(
        <button
          key="encerrada"
          onClick={() => handleStatusChange('encerrada')}
          className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded"
        >
          Encerrar Tarefa
        </button>
      );
    }

    return actions;
  };

  return (
    <Card
      variant="glass"
      className={`relative transition-all duration-200 hover:border-white/20 ${
        task.status === 'encerrada' ? 'opacity-75' : ''
      } ${showActions ? 'z-10' : ''}`}
    >
      {/* Selection Checkbox - Absolute positioned top-left */}
      {showSelection && onSelect && (
        <div className="absolute top-3 left-3 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(task._id)}
            className="h-4 w-4 border-white/50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:text-white"
          />
        </div>
      )}

      <CardContent className={`p-4 ${showSelection ? 'pl-8' : ''}`}>
        <div className="flex items-center justify-between gap-4">
          {/* Left Section - Status Button (Circular) and Title */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {getStatusButton()}
            <div className="flex-1 min-w-0">
              <h4 className={`font-medium text-base ${
                task.status === 'encerrada' ? 'text-green-400 line-through' : 'text-foreground-primary'
              }`}>
                {task.name}
              </h4>
              {task.description && (
                <div className="text-sm text-foreground-tertiary mt-1">
                  <RichTextContent content={task.description} className="text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Center Section - Badges */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className={TaskPriorityColors[task.prioridade]} size="sm">
              {TaskPriorityLabels[task.prioridade]}
            </Badge>
            <Badge className={TaskStatusColors[task.status]} size="sm">
              {TaskStatusLabels[task.status]}
            </Badge>
            <JiraBadge 
              task={task} 
              jiraUrl={jiraUrl}
              size="sm"
            />
          </div>

          {/* Right Section - Hours, Creation Date, and Actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-sm text-foreground-tertiary">
                <Clock className="w-4 h-4" />
                {task.horasEstimadas}h
              </div>
              <div className="flex items-center gap-1 text-xs text-foreground-tertiary">
                <Calendar className="w-3 h-3" />
                {formatTaskDate(task.createdAt)}
              </div>
            </div>

            {/* Actions Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowActions(!showActions)}
                className="text-foreground-tertiary hover:text-foreground-primary p-1 h-8 w-8"
              >
                ⋮
              </Button>

              {showActions && (
                <div className="absolute right-0 top-8 z-50 w-48 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl py-2">
                  {getStatusActions()}
                  {(onEdit || onDuplicate || onDelete) && <hr className="border-white/10 my-2" />}
                  {onEdit && (
                    <button
                      onClick={() => {
                        onEdit(task);
                        setShowActions(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                    >
                      <Edit2 className="w-3 h-3" />
                      Editar
                    </button>
                  )}
                  {onDuplicate && (
                    <button
                      onClick={() => {
                        onDuplicate(task);
                        setShowActions(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                    >
                      <Copy className="w-3 h-3" />
                      Duplicar
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => {
                        onDelete(task._id);
                        setShowActions(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded flex items-center gap-2"
                    >
                      <Trash2 className="w-3 h-3" />
                      Excluir
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      </CardContent>

      {/* Overlay para fechar menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowActions(false)}
        />
      )}
    </Card>
  );
};